// src/pages/LoginPage.js
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  signInWithPopup,
  signInWithEmailAndPassword
} from 'firebase/auth';
import { auth, googleProvider } from '../firebase';
import { useAuth } from '../context/AuthContext';
import crest from '../assets/crest.png';

export default function LoginPage() {
  const navigate              = useNavigate();
  const { user, loading: authLoading, authError, setAuthError } = useAuth();

useEffect(() => {
  if (user && !authLoading) {
    navigate('/dashboard');
  }
}, [user, authLoading, navigate]);

  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');

  // Clear errors when typing
  const clearErrors = () => {
    setError('');
    setAuthError(null);
  };

  // ── Google Sign-in ──────────────────────────────────────────
  const handleGoogleLogin = async () => {
  setLoading(true);
  clearErrors();
  try {
    await signInWithPopup(auth, googleProvider);
    // Don't navigate here — let AuthContext handle it
    // once getMemberByEmail completes successfully
  } catch (err) {
    setError(getFriendlyError(err.code));
    setLoading(false);
  }
  // Note: loading stays true while AuthContext
  // verifies the member — this prevents the double sign-in
  };

  // ── Email + Password Sign-in ────────────────────────────────
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }
    setLoading(true);
    clearErrors();
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(getFriendlyError(err.code));
    } finally {
      setLoading(false);
    }
  };

  // ── Friendly error messages ─────────────────────────────────
  const getFriendlyError = (code) => {
    switch (code) {
      case 'auth/user-not-found':
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return 'Incorrect email or password. Please try again.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please wait a few minutes and try again.';
      case 'auth/popup-closed-by-user':
        return 'Sign-in cancelled. Please try again.';
      default:
        return 'Login failed. Please try again or contact your house representative.';
    }
  };

  const displayError = error || authError;
  if (loading || authLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1B3A6B 0%, #2d5a9e 100%)',
        display: 'flex', alignItems: 'center',
        justifyContent: 'center', fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{ textAlign: 'center', color: '#fff' }}>
          <img
            src={crest}
            alt="Achimota Crest"
            style={{
              width: '80px', height: '80px',
              borderRadius: '50%', objectFit: 'cover',
              border: '3px solid #C9A84C',
              display: 'block',
              margin: '0 auto 20px'
            }}
          />
          <p style={{ fontSize: '16px', opacity: 0.85 }}>
            Verifying your account...
          </p>
          <p style={{ fontSize: '13px', opacity: 0.6 }}>
            This may take a few seconds
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div style={styles.page}>
      <div style={styles.card}>

        {/* Crest & Title */}
        <div style={styles.header}>
          <img
            src={crest}
            alt="Achimota School Crest"
            style={{
              width: '80px', height: '80px',
              borderRadius: '50%',
              objectFit: 'cover',
              border: `3px solid ${GOLD}`,
              display: 'block',
              margin: '0 auto 16px'
            }}
          />
          <h1 style={styles.schoolName}>ACHIMOTA SCHOOL</h1>
          <p style={styles.yearGroup}>1992 Year Group</p>
          <p style={styles.subtitle}>Alumni Portal</p>
        </div>

        {/* Error message */}
        {displayError && (
          <div style={styles.errorBox}>
            {displayError}
          </div>
        )}

        {/* Google Sign-in */}
        <button
          style={{
            ...styles.googleBtn,
            opacity: loading ? 0.7 : 1
          }}
          onClick={handleGoogleLogin}
          disabled={loading}
        >
          <GoogleIcon />
          <span>Sign in with Google</span>
        </button>

        {/* Divider */}
        <div style={styles.divider}>
          <div style={styles.dividerLine} />
          <span style={styles.dividerText}>or</span>
          <div style={styles.dividerLine} />
        </div>

        {/* Email + Password form */}
        <form onSubmit={handleEmailLogin} style={styles.form}>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Email Address</label>
            <input
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); clearErrors(); }}
              placeholder="your@email.com"
              style={styles.input}
              disabled={loading}
            />
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label}>Password</label>
            <input
              type="password"
              value={password}
              onChange={e => { setPassword(e.target.value); clearErrors(); }}
              placeholder="Enter your password"
              style={styles.input}
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            style={{
              ...styles.signInBtn,
              opacity: loading ? 0.7 : 1
            }}
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        {/* Forgot password */}
        <ForgotPassword />

        {/* Footer */}
        <p style={styles.footer}>
          Having trouble? Contact your house representative.
        </p>

      </div>
    </div>
  );
}

// ── Forgot Password ─────────────────────────────────────────
function ForgotPassword() {
  const [show,    setShow]    = useState(false);
  const [email,   setEmail]   = useState('');
  const [sent,    setSent]    = useState(false);
  const [loading, setLoading] = useState(false);

  const handleReset = async () => {
    if (!email) return;
    setLoading(true);
    try {
      const { sendPasswordResetEmail } = await import('firebase/auth');
      await sendPasswordResetEmail(auth, email);
      setSent(true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (!show) return (
    <button style={styles.forgotBtn} onClick={() => setShow(true)}>
      Forgot your password?
    </button>
  );

  if (sent) return (
    <p style={styles.resetSent}>
      ✓ Reset email sent. Check your inbox.
    </p>
  );

  return (
    <div style={styles.resetBox}>
      <p style={styles.resetLabel}>
        Enter your registered email to receive a reset link:
      </p>
      <input
        type="email"
        value={email}
        onChange={e => setEmail(e.target.value)}
        placeholder="your@email.com"
        style={styles.input}
      />
      <button
        style={styles.signInBtn}
        onClick={handleReset}
        disabled={loading}
      >
        {loading ? 'Sending...' : 'Send Reset Link'}
      </button>
    </div>
  );
}

// ── Google Icon SVG ─────────────────────────────────────────
function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" style={{ marginRight: 10 }}>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

// ── Styles ──────────────────────────────────────────────────
const NAVY = '#1B3A6B';
const GOLD = '#C9A84C';

const styles = {
  page: {
    minHeight: '100vh',
    background: `linear-gradient(135deg, ${NAVY} 0%, #2d5a9e 100%)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    fontFamily: 'Arial, sans-serif'
  },
  card: {
    background: '#ffffff',
    borderRadius: '16px',
    padding: '40px 36px',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
  },
  header: {
    textAlign: 'center',
    marginBottom: '28px'
  },
  crestCircle: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: NAVY,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
    border: `3px solid ${GOLD}`
  },
  crestText: {
    color: GOLD,
    fontSize: '36px',
    fontWeight: 'bold'
  },
  schoolName: {
    color: NAVY,
    fontSize: '18px',
    fontWeight: 'bold',
    margin: '0 0 4px',
    letterSpacing: '2px'
  },
  yearGroup: {
    color: GOLD,
    fontSize: '14px',
    fontWeight: 'bold',
    margin: '0 0 4px'
  },
  subtitle: {
    color: '#666',
    fontSize: '13px',
    margin: 0
  },
  errorBox: {
    background: '#fdecea',
    border: '1px solid #f5c6cb',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '16px',
    color: '#c0392b',
    fontSize: '14px',
    textAlign: 'center'
  },
  googleBtn: {
    width: '100%',
    padding: '12px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '600',
    color: '#333',
    transition: 'border-color 0.2s',
    marginBottom: '20px'
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px'
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    background: '#e0e0e0'
  },
  dividerText: {
    padding: '0 12px',
    color: '#999',
    fontSize: '13px'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  label: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#444'
  },
  input: {
    padding: '11px 14px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box'
  },
  signInBtn: {
    width: '100%',
    padding: '13px',
    background: NAVY,
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginTop: '4px'
  },
  forgotBtn: {
    background: 'none',
    border: 'none',
    color: NAVY,
    fontSize: '13px',
    cursor: 'pointer',
    textDecoration: 'underline',
    marginTop: '12px',
    display: 'block',
    width: '100%',
    textAlign: 'center'
  },
  resetBox: {
    marginTop: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  resetLabel: {
    fontSize: '13px',
    color: '#555',
    margin: 0
  },
  resetSent: {
    color: '#1a6b3a',
    fontSize: '14px',
    textAlign: 'center',
    marginTop: '12px'
  },
  footer: {
    textAlign: 'center',
    fontSize: '12px',
    color: '#999',
    marginTop: '20px',
    marginBottom: 0
  }
};